import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useRelayEnvironment } from 'react-relay';

import FlashesStore from 'src/stores/FlashesStore';
import logger from 'src/util/logger';
import {
  calculateTradeSetSummaryBatchSize,
  generateBatchTimeRanges,
} from 'src/util/tradeSetSummary/batchCalculator';

import {
  getTotalRulesCount,
} from '../helpers/dataBatching';
import {
  getPropertyDataFromCache,
  initialiseCache,
} from '../helpers/dataCaching';
import { PropertyShowLoadingActions } from '../reducer/constants';

/**
 * Custom hook that encapsulates all property data fetching.
 *
 * Handles:
 * - Batched data loading with progress tracking via reducer dispatch
 * - Data transformation from GraphQL response to meter objects
 * - Error handling and user feedback
 *
 * Caching is managed by the `dataCaching` module (module-level singleton).
 * This hook delegates to `getPropertyDataFromCache`, passing `propertyId`
 * so the cache layer can reset automatically on property changes.
 * See docs/design-docs/property-show-caching.md for full details.
 *
 * ## Cache bypass
 * To disable caching entirely, import and call `getPropertyDataInBatches`
 * directly (from `../helpers/dataBatching`) instead of going through
 * `getPropertyDataFromCache`. The cache layer internally calls
 * `getPropertyDataInBatches` on a miss, so bypassing the cache requires
 * no other code changes.
 *
 * Loading state is managed externally via the stateDispatchFunc reducer.
 * @param {object} params - Hook parameters
 * @param {string} params.propertyId - The property UUID to fetch data for
 * @param {object} params.dateSpan - Object with start and finish Luxon DateTime objects
 * @param {string} params.timezone - Timezone of the property
 * @param {object} params.initialData - Static/initial property data
 * @param {string} params.historianAggregation - Aggregation level
 * for historian data (e.g., 'PT30M', 'P1D')
 * @param {string} params.tradeAggregation - Aggregation level for trade data
 * @param {Function} [params.stateDispatchFunc] - Dispatch function for loading state reducer
 * @returns {object} Object containing:
 *   - combinedData: {object} Combined property data from GraphQL responses
 */
const usePropertyData = ({
  propertyId,
  dateSpan,
  timezone,
  initialData,
  historianAggregation,
  tradeAggregation,
  stateDispatchFunc,
}) => {
  const environment = useRelayEnvironment();

  const [combinedData, setCombinedData] = useState(null);

  // Flipped to true once the first effect's fetch completes. Used as a
  // dependency of the second effect so that a dateSpan change that
  // arrived while the initial fetch was in-flight is picked up.
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Track if we're currently fetching data to prevent parallel fetches
  const isFetchingData = useRef(false);

  // Per-request cancellation token. Each new request creates a fresh { current: false }
  // object and stores it here. cancelFunc sets the active token's .current to true.
  // Old in-flight requests keep their own token reference, so they stay cancelled even
  // when a newer request starts — preventing the race where resetting a shared boolean
  // would "uncancel" a stale request.
  const cancelTokenRef = useRef({ current: false });

  // Stores the dateSpan at the time of the last cancellation. The second
  // effect compares the current dateSpan against this value: if they match,
  // the effect was triggered by cancelFunc flipping initialLoadComplete (not
  // by a genuine dependency change) and the fetch is skipped. When the user
  // changes dates, the dateSpan differs and the fetch proceeds normally.
  // This replaces a boolean skip flag that could get stale after multiple
  // cancels without an intervening effect run.
  const cancelledDateSpanRef = useRef(null);

  // Mirrors the current dateSpan so cancelFunc (memoised with [] deps) can
  // read the latest value via ref without needing dateSpan as a dependency.
  const dateSpanRef = useRef(dateSpan);
  dateSpanRef.current = dateSpan;

  // Clear the module-level cache when the component unmounts so stale
  // data is not served when navigating back to the same property.
  useEffect(() => () => {
    initialiseCache(null);
  }, []);

  // First effect: Fetch static data (meters, rules) once per propertyId
  useEffect(() => {
    if (!propertyId) {
      logger.error('No property ID found for property data fetching');
      return;
    }

    logger.log('Fetching static property data (meters, rules) for property:', propertyId);

    if (stateDispatchFunc) {
      stateDispatchFunc({ type: PropertyShowLoadingActions.START_REQUEST });
    }

    // Create a fresh cancellation token for this request. Old in-flight requests
    // retain their own (cancelled) token, so they cannot be "uncancelled".
    const cancelToken = { current: false };
    cancelTokenRef.current = cancelToken;

    getPropertyDataFromCache({
      propertyId,
      dateSpan,
      aggregation: historianAggregation,
      fetchParams: {
        propertyId,
        timezone,
        environment,
        tradeAggregation,
        initialData,
      },
      isFetchingRef: isFetchingData,
      cancelledRef: cancelToken,
      stateDispatchFunc,
      onDataFunc: (mergedData) => {
        logger.log('Received merged property data with property:', !!mergedData?.property);
        setInitialLoadComplete(true);
        setCombinedData(mergedData);
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PropertyShowLoadingActions.MARK_FULLY_LOADED });
        }
      },
      onErrorFunc: (fetchError, message) => {
        logger.error('Error fetching property data:', fetchError);
        FlashesStore.flash(FlashesStore.ERROR, message || 'Error fetching property data');
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PropertyShowLoadingActions.SET_ERROR, error: fetchError });
        }
      },
    });
  }, [propertyId, stateDispatchFunc]);

  // Second effect: Fetch time-dependent data when dateSpan changes (using cached static data)
  // Also handles full re-fetch after a cancelled initial load.
  useEffect(() => {
    if (!propertyId || !dateSpan) {
      logger.error('No property ID or date span found for time-dependent data fetching');
      return;
    }

    // Skip this effect until the first effect's fetch has completed.
    if (!initialLoadComplete) {
      logger.log('Skipping second effect - initial load not yet complete');
      return;
    }

    // Skip when the effect was triggered by cancelFunc flipping
    // initialLoadComplete rather than a genuine dateSpan change.
    // cancelledDateSpanRef is a one-shot: set by cancelFunc, cleared here
    // after the first comparison. By the time the user retries (via Apply),
    // the ref is already null, so the retry fetch proceeds unconditionally.
    const cancelledDateSpan = cancelledDateSpanRef.current;
    cancelledDateSpanRef.current = null;

    if (cancelledDateSpan
      && dateSpan?.start?.toMillis() === cancelledDateSpan.start?.toMillis()
      && dateSpan?.finish?.toMillis() === cancelledDateSpan.finish?.toMillis()) {
      logger.log('Skipping fetch - datespan unchanged since cancellation');
      return;
    }

    // Create a fresh cancellation token for this request. Old in-flight
    // requests retain their own (cancelled) token, so they cannot be
    // "uncancelled".
    const cancelToken = { current: false };
    cancelTokenRef.current = cancelToken;

    // Delegate to the read-through cache. On a hit, onDataFunc fires
    // immediately with cached data. On a miss, onBeforeFetchFunc fires
    // first, then the fetch runs and onDataFunc fires with fresh data.
    getPropertyDataFromCache({
      propertyId,
      dateSpan,
      aggregation: historianAggregation,
      fetchParams: {
        propertyId,
        timezone,
        environment,
        tradeAggregation,
        initialData,
      },
      isFetchingRef: isFetchingData,
      cancelledRef: cancelToken,
      stateDispatchFunc,
      onBeforeFetchFunc: ({ segmentsExist }) => {
        if (stateDispatchFunc) {
          stateDispatchFunc({
            type: PropertyShowLoadingActions.START_REQUEST,
          });
          if (segmentsExist) {
            stateDispatchFunc({
              type: PropertyShowLoadingActions.SET_PROPERTY_LOADED,
            });
          }
        }
      },
      onDataFunc: (data, meters, rulesData, fromCache) => {
        setCombinedData(data);
        if (stateDispatchFunc) {
          if (fromCache) {
            const totalRulesCount = getTotalRulesCount(rulesData);
            const { batchSizeHours } = calculateTradeSetSummaryBatchSize({
              start: dateSpan.start,
              finish: dateSpan.finish,
              aggregation: historianAggregation,
              ruleCount: totalRulesCount,
            });
            const batchCount = generateBatchTimeRanges({
              start: dateSpan.start,
              finish: dateSpan.finish,
              batchSizeHours,
            }).length;
            stateDispatchFunc({
              type: PropertyShowLoadingActions.MARK_FULLY_LOADED,
              metersCount: meters.length,
              rulesCount: totalRulesCount,
              batchesLoaded: batchCount,
              batchesTotal: batchCount,
            });
          } else {
            stateDispatchFunc({
              type: PropertyShowLoadingActions.MARK_FULLY_LOADED,
            });
          }
        }
      },
      onErrorFunc: (fetchError, message) => {
        logger.error('Error fetching property data:', fetchError);
        FlashesStore.flash(
          FlashesStore.ERROR,
          message || 'Error fetching property data',
        );
        if (stateDispatchFunc) {
          stateDispatchFunc({
            type: PropertyShowLoadingActions.SET_ERROR,
            error: fetchError,
          });
        }
      },
    });
  }, [
    initialLoadComplete,
    propertyId,
    dateSpan?.start,
    dateSpan?.finish,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    stateDispatchFunc,
  ]);

  const cancelFunc = useCallback(() => {
    cancelTokenRef.current.current = true;
    isFetchingData.current = false;
    // Record the current dateSpan so the second effect can detect that it
    // was triggered by this cancel (initialLoadComplete flip) rather than
    // a genuine dependency change, and skip the unwanted re-fetch.
    cancelledDateSpanRef.current = {
      start: dateSpanRef.current?.start,
      finish: dateSpanRef.current?.finish,
    };
    // Allow subsequent date changes to trigger fetches via the second effect
    setInitialLoadComplete(true);
    logger.log('Data fetch cancelled by user');
  }, []);

  return {
    combinedData,
    cancelFunc,
  };
};

export default usePropertyData;
